import { useRef, useState } from "react";
import { ArrowUpDown, CalendarDays, Search, Truck } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "ui/badge";
import { Button } from "ui/button";
import { Card, CardContent } from "ui/card";
import { Input } from "ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import Pagination from "ui/pagination";

type TourMovement = {
  id: string;
  tour: string;
  operator: string;
  tourType: string;
  date: string;
  pax: number;
  movement:
    | "Arrival Transfer"
    | "Inter-venue Transfer"
    | "Atraaf Travel"
    | "Departure Transfer";
  routeId: string;
  start: string;
  source: "Tour" | "Atraaf Master";
  lawazimPrice: number;
};

type MovementSortKey =
  | "movement"
  | "tour"
  | "pickup"
  | "pickupDate"
  | "pickupTime"
  | "dropoff"
  | "dropoffDate"
  | "dropoffTime"
  | "pax"
  | "status";
type SortDirection = "asc" | "desc";
type AssignmentStatus = "scheduled" | "in-route" | "completed";
type FleetStatus = "available" | "assigned" | "in-route" | "maintenance";
type FleetSource = "internal" | "regular-vendor" | "external-vendor";

type Vehicle = {
  id: string;
  registration: string;
  type: string;
  capacity: number;
  source: FleetSource;
  vendor: string;
  location: string;
  status: FleetStatus;
  driverId?: string;
  nextMovement?: string;
};

type TransportRoute = {
  id: string;
  name: string;
  from: string;
  to: string;
  distance: number;
  duration: number;
  checkpoints: number;
  permit: string;
};

type Assignment = {
  id: string;
  vehicleId: string;
  driverId?: string;
  routeId: string;
  movement: string;
  group: string;
  pax: number;
  start: string;
  end: string;
  hours: number;
  status: AssignmentStatus;
  movementId?: string;
  price?: number;
};

type RequirementsProps = {
  vehicles: Vehicle[];
  routes: TransportRoute[];
  assignments: Assignment[];

  setAssignments: React.Dispatch<React.SetStateAction<Assignment[]>>;

  setVehicles: React.Dispatch<React.SetStateAction<Vehicle[]>>;

  onAssignMovement: (request: TourMovement) => void;

  draggedVehicleId: string;
  setDraggedVehicleId: React.Dispatch<React.SetStateAction<string>>;
};

const TOUR_MOVEMENTS: TourMovement[] = [
  {
    id: "TM-01",
    tour: "Ziyarat Najaf 2026",
    operator: "Al Burhani Tours",
    tourType: "Faiz",
    date: "2026-08-12",
    pax: 42,
    movement: "Arrival Transfer",
    routeId: "R-01",
    start: "08:45",
    source: "Tour",
    lawazimPrice: 840,
  },
  {
    id: "TM-02",
    tour: "Ziyarat Najaf 2026",
    operator: "Al Burhani Tours",
    tourType: "Faiz",
    date: "2026-08-12",
    pax: 42,
    movement: "Atraaf Travel",
    routeId: "R-05",
    start: "14:30",
    source: "Atraaf Master",
    lawazimPrice: 1050,
  },
  {
    id: "TM-03",
    tour: "Ziyarat Najaf 2026",
    operator: "Al Burhani Tours",
    tourType: "Faiz",
    date: "2026-08-12",
    pax: 42,
    movement: "Inter-venue Transfer",
    routeId: "R-03",
    start: "19:00",
    source: "Tour",
    lawazimPrice: 630,
  },
  {
    id: "TM-04",
    tour: "Ziyarat Najaf 2026",
    operator: "Al Burhani Tours",
    tourType: "Faiz",
    date: "2026-08-12",
    pax: 42,
    movement: "Departure Transfer",
    routeId: "R-08",
    start: "22:00",
    source: "Tour",
    lawazimPrice: 840,
  },
  {
    id: "TM-05",
    tour: "Miqaat Karbala 2026",
    operator: "Saifee Tours & Travel",
    tourType: "Miqaat",
    date: "2026-08-12",
    pax: 28,
    movement: "Arrival Transfer",
    routeId: "R-02",
    start: "11:20",
    source: "Tour",
    lawazimPrice: 700,
  },
  {
    id: "TM-06",
    tour: "Miqaat Karbala 2026",
    operator: "Saifee Tours & Travel",
    tourType: "Miqaat",
    date: "2026-08-12",
    pax: 28,
    movement: "Atraaf Travel",
    routeId: "R-06",
    start: "16:00",
    source: "Atraaf Master",
    lawazimPrice: 980,
  },
  {
    id: "TM-07",
    tour: "Miqaat Karbala 2026",
    operator: "Saifee Tours & Travel",
    tourType: "Miqaat",
    date: "2026-08-12",
    pax: 28,
    movement: "Inter-venue Transfer",
    routeId: "R-07",
    start: "21:00",
    source: "Tour",
    lawazimPrice: 560,
  },
  {
    id: "TM-08",
    tour: "Instant Najaf Test Tour",
    operator: "Najaf Instant Tours",
    tourType: "Instant",
    date: "2026-08-12",
    pax: 12,
    movement: "Arrival Transfer",
    routeId: "R-01",
    start: "09:30",
    source: "Tour",
    lawazimPrice: 240,
  },
  {
    id: "TM-09",
    tour: "Instant Najaf Test Tour",
    operator: "Najaf Instant Tours",
    tourType: "Instant",
    date: "2026-08-12",
    pax: 12,
    movement: "Inter-venue Transfer",
    routeId: "R-03",
    start: "12:30",
    source: "Tour",
    lawazimPrice: 300,
  },
  {
    id: "TM-10",
    tour: "Instant Najaf Test Tour",
    operator: "Najaf Instant Tours",
    tourType: "Instant",
    date: "2026-08-12",
    pax: 12,
    movement: "Atraaf Travel",
    routeId: "R-05",
    start: "15:00",
    source: "Atraaf Master",
    lawazimPrice: 360,
  },
  {
    id: "TM-11",
    tour: "External Hotel Test Tour",
    operator: "External Hotel Operator",
    tourType: "External",
    date: "2026-08-12",
    pax: 31,
    movement: "Arrival Transfer",
    routeId: "R-02",
    start: "10:10",
    source: "Tour",
    lawazimPrice: 620,
  },
  {
    id: "TM-12",
    tour: "External Hotel Test Tour",
    operator: "External Hotel Operator",
    tourType: "External",
    date: "2026-08-12",
    pax: 31,
    movement: "Departure Transfer",
    routeId: "R-08",
    start: "18:10",
    source: "Tour",
    lawazimPrice: 620,
  },
  {
    id: "TM-13",
    tour: "Burhani Family Ziyarat",
    operator: "Burhani Holidays",
    tourType: "Family",
    date: "2026-08-12",
    pax: 18,
    movement: "Arrival Transfer",
    routeId: "R-01",
    start: "07:30",
    source: "Tour",
    lawazimPrice: 420,
  },
  {
    id: "TM-14",
    tour: "Karbala Heritage Group",
    operator: "Al Murtaza Travel",
    tourType: "Ziyarat",
    date: "2026-08-12",
    pax: 36,
    movement: "Inter-venue Transfer",
    routeId: "R-07",
    start: "13:15",
    source: "Tour",
    lawazimPrice: 720,
  },
  {
    id: "TM-15",
    tour: "Najaf Atraaf Delegation",
    operator: "Faiz Travel Services",
    tourType: "Delegation",
    date: "2026-08-12",
    pax: 24,
    movement: "Atraaf Travel",
    routeId: "R-05",
    start: "10:45",
    source: "Atraaf Master",
    lawazimPrice: 600,
  },
  {
    id: "TM-16",
    tour: "Baghdad Arrival Group",
    operator: "Noor Al Safar",
    tourType: "Arrival",
    date: "2026-08-12",
    pax: 44,
    movement: "Arrival Transfer",
    routeId: "R-02",
    start: "17:20",
    source: "Tour",
    lawazimPrice: 880,
  },
  {
    id: "TM-17",
    tour: "Samarra Day Ziyarat",
    operator: "Karbala Tours Company",
    tourType: "Excursion",
    date: "2026-08-12",
    pax: 32,
    movement: "Inter-venue Transfer",
    routeId: "R-04",
    start: "06:15",
    source: "Tour",
    lawazimPrice: 960,
  },
];

const addHoursToTime = (start: string, hours: number) => {
  const [hour, minute] = start.split(":").map(Number);
  const total = (hour * 60 + minute + Math.round(hours * 60)) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

export function Requirements({
  vehicles,
  routes,
  assignments,
  setAssignments,
  setVehicles,
  onAssignMovement,
  draggedVehicleId,
  setDraggedVehicleId,
}: RequirementsProps) {
  const [date, setDate] = useState("2026-08-12");
  const [selectedTour, setSelectedTour] = useState("all");
  const [movementPage, setMovementPage] = useState(1);
  const [movementPageSize, setMovementPageSize] = useState(10);
  const [selectedMovementIds, setSelectedMovementIds] = useState<string[]>([]);
  const [bulkVehicleId, setBulkVehicleId] = useState("");
  const [movementSearch, setMovementSearch] = useState("");
  const [movementSort, setMovementSort] =
    useState<MovementSortKey>("pickupTime");
  const [movementSortDirection, setMovementSortDirection] =
    useState<SortDirection>("asc");
  const dateInputRef = useRef<HTMLInputElement>(null);

  const tourNames = Array.from(
    new Set(
      TOUR_MOVEMENTS.filter((item) => item.date === date).map(
        (item) => item.tour,
      ),
    ),
  );

  const filteredTourMovements = TOUR_MOVEMENTS.filter(
    (item) =>
      item.date === date &&
      (selectedTour === "all" || item.tour === selectedTour),
  );

  const flatTransportMovements = filteredTourMovements
    .filter((item) => {
      const route = routes.find((routeItem) => routeItem.id === item.routeId);
      const query = movementSearch.trim().toLowerCase();
      return (
        !query ||
        [
          item.tour,
          item.operator,
          item.movement,
          route?.name,
          route?.from,
          route?.to,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query))
      );
    })
    .sort((left, right) => {
      const leftRoute = routes.find((item) => item.id === left.routeId);
      const rightRoute = routes.find((item) => item.id === right.routeId);
      const values: Record<
        MovementSortKey,
        [string | number, string | number]
      > = {
        movement: [left.movement, right.movement],
        tour: [left.tour, right.tour],
        pickup: [leftRoute?.from || "", rightRoute?.from || ""],
        pickupDate: [left.date, right.date],
        pickupTime: [left.start, right.start],
        dropoff: [leftRoute?.to || "", rightRoute?.to || ""],
        dropoffDate: [left.date, right.date],
        dropoffTime: [
          leftRoute ? addHoursToTime(left.start, leftRoute.duration) : "",
          rightRoute ? addHoursToTime(right.start, rightRoute.duration) : "",
        ],
        pax: [left.pax, right.pax],
        status: [
          assignments.some((item) => item.movementId === left.id)
            ? "scheduled"
            : "unscheduled",
          assignments.some((item) => item.movementId === right.id)
            ? "scheduled"
            : "unscheduled",
        ],
      };
      const [leftValue, rightValue] = values[movementSort];
      const result =
        typeof leftValue === "number" && typeof rightValue === "number"
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue));
      return movementSortDirection === "asc" ? result : -result;
    });

  const paginatedTransportMovements = flatTransportMovements.slice(
    (movementPage - 1) * movementPageSize,
    movementPage * movementPageSize,
  );

  const changeMovementSort = (key: MovementSortKey) => {
    setMovementSortDirection((current) =>
      movementSort === key && current === "asc" ? "desc" : "asc",
    );
    setMovementSort(key);
  };

  const assignSelectedTransfers = (targetVehicleId = bulkVehicleId) => {
    const vehicle = vehicles.find((item) => item.id === targetVehicleId);
    const selectedRequests = flatTransportMovements.filter((item) =>
      selectedMovementIds.includes(item.id),
    );
    if (!vehicle || !selectedRequests.length) {
      toast.error("Select one or more transfer lines and a vehicle");
      return;
    }
    if (vehicle.status === "maintenance") {
      toast.error("A vehicle under maintenance cannot be scheduled");
      return;
    }
    const newAssignments = selectedRequests
      .filter(
        (request) =>
          !assignments.some(
            (assignment) => assignment.movementId === request.id,
          ),
      )
      .map((request, index): Assignment => {
        const route = routes.find((item) => item.id === request.routeId);
        const hours = route?.duration ?? 0;
        return {
          id: `AS-BULK-${Date.now()}-${index}`,
          vehicleId: vehicle.id,
          driverId: vehicle.driverId,
          routeId: request.routeId,
          movement: request.movement,
          group: request.tour,
          pax: request.pax,
          start: request.start,
          end: addHoursToTime(request.start, hours),
          hours,
          status: "scheduled",
          movementId: request.id,
        };
      });
    if (!newAssignments.length) {
      toast.error("The selected transfer lines are already scheduled");
      return;
    }
    setAssignments((current) => [...current, ...newAssignments]);
    setVehicles((current) =>
      current.map((item) =>
        item.id === vehicle.id ? { ...item, status: "assigned" } : item,
      ),
    );
    setSelectedMovementIds([]);
    setBulkVehicleId("");
    toast.success(
      `${newAssignments.length} transfer line${newAssignments.length === 1 ? "" : "s"} assigned to ${vehicle.registration}`,
    );
  };

  return (
    <Card>
      <div className="contents">
        <Card
          className="order-3 h-full overflow-hidden rounded-xl border-slate-200 bg-white shadow-sm"
          style={{ gap: "0px" }}
        >
          <CardContent
            className="flex h-full flex-col"
            style={{ padding: "0px 0px 10px 0px" }}
          >
            <div className="grid items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2.5 lg:grid-cols-[minmax(300px,1.2fr)_minmax(200px,1fr)_minmax(200px,1fr)_160px]">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <Truck className="h-4 w-4 text-sky-700" />
                  Day-wise Transfer Requirements
                </div>
                <div className="text-[11px] text-slate-500">
                  Select transfer lines across tours and allocate them to one
                  vehicle
                </div>
              </div>
              <div className="hidden lg:block" />
              <Select value={selectedTour} onValueChange={setSelectedTour}>
                <SelectTrigger className="h-8 w-full bg-white text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tours</SelectItem>
                  {tourNames.map((tour) => (
                    <SelectItem key={tour} value={tour}>
                      {tour}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex w-full items-center gap-1 rounded-md border border-slate-200 bg-white px-2">
                <button
                  type="button"
                  onClick={() => dateInputRef.current?.showPicker()}
                  className="flex h-7 w-7 shrink-0 items-center justify-center"
                  aria-label="Open date picker"
                >
                  <CalendarDays className="h-4 w-4 text-sky-600" />
                </button>

                <Input
                  ref={dateInputRef}
                  type="date"
                  value={date}
                  onChange={(event) => {
                    setDate(event.target.value);
                    setSelectedTour("all");
                    setSelectedMovementIds([]);
                  }}
                  className="h-7 flex-1 border-0 p-0 text-[10px] text-sky-600 shadow-none focus-visible:ring-0 [&::-webkit-calendar-picker-indicator]:hidden"
                />
              </div>
            </div>
            <div className="grid gap-2 border-b border-slate-200 px-3 py-2 lg:grid-cols-[minmax(300px,1.2fr)_minmax(200px,1fr)_minmax(200px,1fr)_160px]">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={movementSearch}
                  onChange={(event) => setMovementSearch(event.target.value)}
                  placeholder="Search tour, operator, route or airport…"
                  className="h-8 pl-7 text-[9px]"
                />
              </div>
              <div />
              <Select value={bulkVehicleId} onValueChange={setBulkVehicleId}>
                <SelectTrigger className="h-8 text-[9px]">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles
                    .filter((vehicle) => vehicle.status !== "maintenance")
                    .map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.registration} · {vehicle.type} ·{" "}
                        {vehicle.capacity} seats
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => assignSelectedTransfers()}
                disabled={!selectedMovementIds.length || !bulkVehicleId}
                className="h-8 w-[160px] bg-sky-600 px-3 text-[9px] text-white hover:bg-sky-700"
              >
                Assign {selectedMovementIds.length || ""} selected
              </Button>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer hover:bg-sky-100"
                    >
                      <input
                        type="checkbox"
                        aria-label="Select all unscheduled transfers"
                        checked={
                          flatTransportMovements.filter(
                            (item) =>
                              !assignments.some(
                                (assignment) =>
                                  assignment.movementId === item.id,
                              ),
                          ).length > 0 &&
                          flatTransportMovements
                            .filter(
                              (item) =>
                                !assignments.some(
                                  (assignment) =>
                                    assignment.movementId === item.id,
                                ),
                            )
                            .every((item) =>
                              selectedMovementIds.includes(item.id),
                            )
                        }
                        onChange={(event) =>
                          setSelectedMovementIds(
                            event.target.checked
                              ? flatTransportMovements
                                  .filter(
                                    (item) =>
                                      !assignments.some(
                                        (assignment) =>
                                          assignment.movementId === item.id,
                                      ),
                                  )
                                  .map((item) => item.id)
                              : [],
                          )
                        }
                      />
                    </TableHead>
                    {(
                      [
                        ["movement", "Transfer"],
                        ["tour", "Tour / Operator"],
                        ["pickup", "Pickup Location"],
                        ["pickupDate", "Pickup Date"],
                        ["pickupTime", "Pickup Time"],
                        ["dropoff", "Drop-off Location"],
                        ["dropoffDate", "Drop-off Date"],
                        ["dropoffTime", "Drop-off Time"],
                        ["pax", "Pax"],
                        ["status", "Status"],
                      ] as [MovementSortKey, string][]
                    ).map(([key, label]) => (
                      <TableHead
                        key={key}
                        className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#5B9BD5]"
                      >
                        <button
                          type="button"
                          onClick={() => changeMovementSort(key)}
                          className="flex items-center text-xs gap-1 whitespace-nowrap text-[#5B9BD5]"
                        >
                          {label}
                          <ArrowUpDown className="h-3 w-3" />
                        </button>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTransportMovements.map((request) => {
                    const route = routes.find(
                      (item) => item.id === request.routeId,
                    );
                    const assignment = assignments.find(
                      (item) => item.movementId === request.id,
                    );
                    const vehicle = vehicles.find(
                      (item) => item.id === assignment?.vehicleId,
                    );
                    return (
                      <TableRow
                        key={request.id}
                        draggable={!assignment}
                        onDragStart={() => {
                          if (!selectedMovementIds.includes(request.id))
                            setSelectedMovementIds((current) => [
                              ...current,
                              request.id,
                            ]);
                        }}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                          event.preventDefault();
                          if (draggedVehicleId && !assignment) {
                            setSelectedMovementIds((current) =>
                              Array.from(new Set([...current, request.id])),
                            );
                            setBulkVehicleId(draggedVehicleId);
                            setDraggedVehicleId("");
                          }
                        }}
                        className={`border-sky-100 text-[9px] ${selectedMovementIds.includes(request.id) ? "cursor-grab bg-sky-50" : "hover:bg-sky-50/40"}`}
                      >
                        <TableCell className="px-4 py-2">
                          <input
                            type="checkbox"
                            disabled={Boolean(assignment)}
                            checked={selectedMovementIds.includes(request.id)}
                            onChange={() =>
                              setSelectedMovementIds((current) =>
                                current.includes(request.id)
                                  ? current.filter((id) => id !== request.id)
                                  : [...current, request.id],
                              )
                            }
                          />
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          <Badge
                            variant="outline"
                            className={`justify-center px-1 py-0 text-[7px] ${request.movement === "Arrival Transfer" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : request.movement === "Departure Transfer" ? "border-rose-200 bg-rose-50 text-rose-700" : request.movement === "Atraaf Travel" ? "border-violet-200 bg-violet-50 text-violet-700" : "border-sky-200 bg-sky-50 text-sky-700"}`}
                          >
                            {request.movement.replace(" Transfer", "")}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[210px] px-4 py-3">
                          <div className="text-sm font-medium text-foreground">
                            {request.tour}
                          </div>
                          <div className="text-xs text-muted-foreground pt-1">
                            {request.operator}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[155px] truncate px-4 py-3">
                          <span className="text-xs text-muted-foreground">
                            {route?.from || "—"}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-muted-foreground">
                            {request.date}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-muted-foreground">
                            {request.start}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-[155px] truncate px-4 py-3">
                          <span className="text-xs text-muted-foreground">
                            {route?.to || "—"}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-muted-foreground">
                            {request.date}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-muted-foreground">
                            {route
                              ? addHoursToTime(request.start, route.duration)
                              : "—"}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-muted-foreground">
                            {request.pax}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          {assignment ? (
                            <div className="flex flex-col">
                              <div className="truncate text-sm font-medium text-emerald-700">
                                {vehicle?.registration}
                              </div>
                            </div>
                          ) : (
                            <Button
                              onClick={() => onAssignMovement(request)}
                              variant="outline"
                              className="h-6 px-2 text-[7px]"
                            >
                              Assign
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!flatTransportMovements.length && (
                    <TableRow>
                      <TableCell
                        colSpan={11}
                        className="p-8 text-center text-xs text-slate-500"
                      >
                        No transfer requirements for the selected day.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
          {flatTransportMovements.length > movementPageSize && (
            <Pagination
              totalItems={flatTransportMovements.length}
              itemsPerPage={movementPageSize}
              currentPage={movementPage}
              onPageChange={setMovementPage}
              onPageSizeChange={(size) => {
                setMovementPageSize(size);
                setMovementPage(1);
              }}
              label="transfer requirements"
            />
          )}
        </Card>
      </div>
    </Card>
  );
}
